import React from 'react';
import {
  Search,
  Eye,
  SlidersHorizontal,
  Ban,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Star,
  Zap,
  Users,
} from 'lucide-react';
import type { AdminUserListItem } from '@nova-mine/shared';

interface UserTableProps {
  users: AdminUserListItem[];
  total: number;
  limit: number;
  offset: number;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  statusFilter: string;
  onStatusFilterChange: (s: string) => void;
  onPageChange: (newOffset: number) => void;
  onInspectUser: (userId: string) => void;
  onOpenAdjust: (user: AdminUserListItem) => void;
  onToggleBan: (user: AdminUserListItem) => void;
  isLoading?: boolean;
}

export const UserTable: React.FC<UserTableProps> = ({
  users,
  total,
  limit,
  offset,
  searchQuery,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  onPageChange,
  onInspectUser,
  onOpenAdjust,
  onToggleBan,
  isLoading = false,
}) => {
  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.max(Math.ceil(total / limit), 1);

  return (
    <div className="w-full glass-panel rounded-3xl border border-white/10 overflow-hidden space-y-4">
      {/* Search & Filters Toolbar */}
      <div className="p-5 border-b border-white/5 flex flex-wrap items-center justify-between gap-4 bg-space-950/40">
        <div>
          <h3 className="text-base font-black text-white flex items-center space-x-2">
            <Users className="w-5 h-5 text-purple-400" />
            <span>User Accounts & Asset Registry</span>
          </h3>
          <p className="text-xs text-gray-400 mt-0.5">
            Searchable registry showing balances, lifetime earnings, miner counts, and referral attribution.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Status Filter */}
          <div className="flex bg-space-900 border border-white/10 rounded-xl p-1 text-xs font-bold">
            {['all', 'active', 'banned'].map((s) => (
              <button
                key={s}
                onClick={() => onStatusFilterChange(s)}
                className={`px-3 py-1.5 rounded-lg capitalize transition-all ${
                  statusFilter === s
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {s}
              </button>
            ))}
          </div>

          {/* Search Box */}
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search Telegram ID, username, name..."
              className="bg-space-900 border border-white/10 rounded-xl pl-9 pr-4 py-2 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-purple-400 w-64"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead className="bg-space-900/90 border-b border-white/5 text-gray-400 font-bold uppercase tracking-wider text-[11px]">
            <tr>
              <th className="px-5 py-3.5">User</th>
              <th className="px-5 py-3.5">Telegram ID</th>
              <th className="px-5 py-3.5">Available Balance</th>
              <th className="px-5 py-3.5">Lifetime Earned</th>
              <th className="px-5 py-3.5">Active Miners</th>
              <th className="px-5 py-3.5">Referral Attribution</th>
              <th className="px-5 py-3.5">Status</th>
              <th className="px-5 py-3.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5 text-gray-300">
            {isLoading ? (
              <tr>
                <td colSpan={8} className="px-6 py-12 text-center text-gray-400">
                  <div className="animate-pulse space-y-2 max-w-sm mx-auto">
                    <div className="h-4 bg-space-800 rounded w-full" />
                    <div className="h-4 bg-space-850 rounded w-4/5 mx-auto" />
                  </div>
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                  No matching user accounts found.
                </td>
              </tr>
            ) : (
              users.map((u) => (
                <tr key={u.id} className="hover:bg-white/[0.02] transition-colors">
                  {/* Name & Username */}
                  <td className="px-5 py-4 font-semibold text-white">
                    <div className="flex items-center space-x-2">
                      <span>{u.first_name}</span>
                    </div>
                    <div className="text-[11px] text-gray-400 font-normal">
                      {u.username ? `@${u.username}` : `ID: ${u.id.slice(0, 8)}...`}
                    </div>
                  </td>

                  {/* Telegram ID */}
                  <td className="px-5 py-4 font-mono text-gray-300">{u.telegram_id}</td>

                  {/* Available Balance */}
                  <td className="px-5 py-4 font-mono font-bold text-nova-gold">
                    {u.balance.toLocaleString()} NOVA
                  </td>

                  {/* Lifetime Earnings */}
                  <td className="px-5 py-4 font-mono text-emerald-400">
                    {u.lifetime_earnings.toLocaleString()} NOVA
                  </td>

                  {/* Active Miners */}
                  <td className="px-5 py-4">
                    <div className="flex items-center space-x-1.5">
                      <span className="px-1.5 py-0.5 rounded text-[10px] bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 font-bold">
                        Main: {u.main_miner_status === 'active' ? 'ON' : 'OFF'}
                      </span>
                      {u.auto_miner_unlocked && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] bg-purple-500/10 text-purple-400 border border-purple-500/20 font-bold">
                          Auto
                        </span>
                      )}
                      {u.stars_miners_count > 0 && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] bg-amber-500/10 text-nova-gold border border-amber-500/20 font-bold flex items-center space-x-0.5">
                          <Star className="w-2.5 h-2.5 fill-nova-gold" />
                          <span>{u.stars_miners_count}x</span>
                        </span>
                      )}
                    </div>
                  </td>

                  {/* Referral Attribution */}
                  <td className="px-5 py-4 text-[11px]">
                    <div className="text-gray-300 font-bold">{u.referral_count} Invited</div>
                    <div className="text-gray-500">
                      Ref: {u.referrer_telegram_id ? u.referrer_telegram_id : 'Direct'}
                    </div>
                  </td>

                  {/* Status Badge */}
                  <td className="px-5 py-4">
                    {u.is_banned ? (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-500/20 text-red-400 border border-red-500/30">
                        BANNED
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                        ACTIVE
                      </span>
                    )}
                  </td>

                  {/* Actions */}
                  <td className="px-5 py-4 text-right">
                    <div className="flex items-center justify-end space-x-1.5">
                      <button
                        onClick={() => onInspectUser(u.id)}
                        className="p-1.5 rounded-lg bg-space-850 hover:bg-space-800 text-gray-300 hover:text-white border border-white/10 transition-colors"
                        title="Inspect User Details"
                      >
                        <Eye className="w-3.5 h-3.5 text-purple-400" />
                      </button>
                      <button
                        onClick={() => onOpenAdjust(u)}
                        className="p-1.5 rounded-lg bg-space-850 hover:bg-space-800 text-gray-300 hover:text-white border border-white/10 transition-colors"
                        title="Adjust Balance"
                      >
                        <SlidersHorizontal className="w-3.5 h-3.5 text-nova-gold" />
                      </button>
                      <button
                        onClick={() => onToggleBan(u)}
                        className={`p-1.5 rounded-lg border transition-colors ${
                          u.is_banned
                            ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/20'
                            : 'bg-red-500/10 border-red-500/20 text-red-400 hover:bg-red-500/20'
                        }`}
                        title={u.is_banned ? 'Unban User' : 'Ban User'}
                      >
                        <Ban className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      <div className="p-4 border-t border-white/5 flex items-center justify-between text-xs text-gray-400 bg-space-950/40">
        <div>
          Showing {users.length > 0 ? offset + 1 : 0} to {Math.min(offset + limit, total)} of {total.toLocaleString()} users
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={() => onPageChange(Math.max(offset - limit, 0))}
            disabled={offset === 0 || isLoading}
            className="p-2 rounded-xl bg-space-900 border border-white/10 text-gray-300 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="font-mono font-bold text-gray-200">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => onPageChange(offset + limit)}
            disabled={offset + limit >= total || isLoading}
            className="p-2 rounded-xl bg-space-900 border border-white/10 text-gray-300 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
